import { useCallback, useEffect, useState } from 'react';
import { toErrorMessage } from '../api/client';

interface ApiDataState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * Minimal fetch-on-mount helper. Every page in the Razor portal loaded its data in OnGetAsync
 * and rendered once; this reproduces that with an explicit loading and error state, plus a
 * reload() for pages that mutate and need to re-read.
 *
 * `deps` is the dependency list that should retrigger the fetch (page number, filters, ...).
 */
export function useApiData<T>(
  loader: () => Promise<T>,
  deps: unknown[] = [],
): ApiDataState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const reload = useCallback(() => setReloadToken((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    loader()
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err) => {
        if (!cancelled) setError(toErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, reloadToken]);

  return { data, loading, error, reload };
}
